/**
 * Text layout for Uni Forms report cells. Mirrors ``uni_forms/report_text.py``.
 *
 * * keep — show the answer unchanged
 * * truncate — keep the first N characters and append ``...``
 * * line_break — after every N characters, break at the next space
 * * apply_to_question_column — use that layout on the question column too
 */

export const TEXT_PRESENTATION_MODES = ['keep', 'truncate', 'line_break'];
export const MAX_TEXT_CHARS = 2000;

export function isQuestionColumn(key) {
  return key === 'question';
}

export function normalizeTextPresentation(raw) {
  const mode = TEXT_PRESENTATION_MODES.includes(raw?.mode) ? raw.mode : 'keep';
  const maxChars = positiveInt(raw?.max_chars);
  const applyToQuestionColumn = asBool(raw?.apply_to_question_column);
  if (mode !== 'keep' && maxChars == null) {
    return { mode: 'keep', max_chars: null, apply_to_question_column: applyToQuestionColumn };
  }
  return {
    mode,
    max_chars: mode === 'keep' ? null : maxChars,
    apply_to_question_column: applyToQuestionColumn,
  };
}

export function textPresentationError(raw) {
  if (raw == null) return '';
  const mode = raw.mode || 'keep';
  if (!TEXT_PRESENTATION_MODES.includes(mode)) {
    return 'Choose keep, truncate, or a line break for long text.';
  }
  if (mode === 'keep') return '';
  if (positiveInt(raw.max_chars) == null) {
    return 'Enter a whole number of characters from 1 to 2000.';
  }
  return '';
}

export function textPresentationForApi(config) {
  const raw = config?.text_presentation || { mode: 'keep' };
  const mode = raw.mode || 'keep';
  return {
    mode,
    max_chars: mode === 'keep' ? null : positiveInt(raw.max_chars),
    apply_to_question_column: asBool(raw.apply_to_question_column),
  };
}

export function presentReportText(value, presentation, options) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const text = String(value);
  const settings = normalizeTextPresentation(presentation);
  if (
    options?.questionColumn
    && !settings.apply_to_question_column
    && settings.mode !== 'keep'
  ) {
    return singleLine(text);
  }
  const limit = settings.max_chars;
  if (settings.mode === 'keep' || !limit || !text) return text;
  if (settings.mode === 'truncate') {
    return text.length <= limit ? text : `${text.slice(0, limit)}...`;
  }
  if (settings.mode === 'line_break') {
    return text.split('\n').map((line) => breakLine(line, limit)).join('\n');
  }
  return text;
}

export function formatReportPeriod(meta, columnCount) {
  if (!meta) return '';
  if (meta.period_label) return meta.period_label;
  const parts = [];
  if (meta.report_day) {
    parts.push(`Day: ${meta.report_day}`);
  } else if (meta.date_from && meta.date_to) {
    parts.push(`${meta.date_from} → ${meta.date_to}`);
  } else if (meta.date_from || meta.date_to) {
    parts.push(String(meta.date_from || meta.date_to));
  }
  if (meta.row_count !== undefined && meta.row_count !== null) {
    parts.push(countLabel(meta.row_count, 'row', 'rows'));
  }
  const cols = columnCount ?? meta.column_count;
  if (cols !== undefined && cols !== null) {
    parts.push(countLabel(cols, 'column', 'columns'));
  }
  return parts.join(' · ');
}

export function formatGeneratedLabel(meta) {
  if (!meta) return '';
  if (meta.generated_at_label) return String(meta.generated_at_label);
  if (!meta.generated_at) return '';
  const date = new Date(meta.generated_at);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function asBool(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    return lowered === '1' || lowered === 'true' || lowered === 'yes' || lowered === 'y';
  }
  return false;
}

function singleLine(text) {
  if (!/[\r\n]/.test(text)) return text;
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ');
}

function positiveInt(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    return null;
  }
  const number = Number(value);
  if (!Number.isInteger(number) || number < 1 || number > MAX_TEXT_CHARS) return null;
  return number;
}

function countLabel(value, singular, plural) {
  const number = Number(value);
  const safe = Number.isFinite(number) ? number : 0;
  return `${safe} ${safe === 1 ? singular : plural}`;
}

function breakLine(line, limit) {
  const parts = [];
  let rest = line;
  while (rest.length > limit) {
    const idx = rest.indexOf(' ', limit);
    if (idx === -1) {
      parts.push(rest);
      rest = '';
      break;
    }
    parts.push(rest.slice(0, idx));
    rest = rest.slice(idx + 1).replace(/^ +/, '');
  }
  if (rest) parts.push(rest);
  return parts.join('\n');
}
